import { createSlice } from "@reduxjs/toolkit";
import type { OrderSliceType, OrderType } from "../../types/order/order.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  ListOrderPayload,
  UpdateOrderStatusPayload,
} from "../../types/order/order.payload";
import type { Pagination } from "../../types/common/pagination";

const initialState: OrderSliceType = {
  loading: {
    list: false,
    detail: false,
    updateStatus: false,
    delete: false,
  },
  error: null,
  order: null,
  orders: [],
  pagination: null,
};

export const getOrders = createApiThunk<
  { orders: OrderType[]; pagination: Pagination },
  ListOrderPayload
>(
  "order/getOrders",
  async (query) =>
    (await axiosClient.get(API.order.list, { params: query })).data.data,
  "Lấy danh sách đơn hàng thất bại",
);

export const getOrder = createApiThunk<{ order: OrderType }, string>(
  "order/getOrder",
  async (id) => (await axiosClient.get(API.order.detail(id))).data.data,
  "Lấy chi tiết đơn hàng thất bại",
);

export const updateOrderStatus = createApiThunk<
  { order: OrderType },
  UpdateOrderStatusPayload
>(
  "order/updateStatus",
  async ({ id, status, cancelReason }) =>
    (await axiosClient.patch(API.order.updateStatus(id), { status, cancelReason }))
      .data.data,
  "Cập nhật trạng thái đơn hàng thất bại",
);

export const deleteOrder = createApiThunk<string, string>(
  "order/deleteOrder",
  async (id) => {
    await axiosClient.delete(API.order.delete(id));
    return id;
  },
  "Xóa đơn hàng thất bại",
);

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    clearOrder: (state) => {
      state.order = null;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: getOrders,
      loadingKey: "list",
      fallbackError: "Lấy danh sách đơn hàng thất bại",
      onSuccess: (state, payload) => {
        state.orders = payload.orders;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: getOrder,
      loadingKey: "detail",
      fallbackError: "Lấy chi tiết đơn hàng thất bại",
      onSuccess: (state, payload) => {
        state.order = payload.order;
      },
    });
    addAsyncCases({
      builder,
      thunk: updateOrderStatus,
      loadingKey: "updateStatus",
      fallbackError: "Cập nhật trạng thái đơn hàng thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.order;
        const idx = state.orders.findIndex((o) => o._id === updated._id);
        if (idx !== -1) state.orders[idx] = updated;
        if (state.order?._id === updated._id) state.order = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteOrder,
      loadingKey: "delete",
      fallbackError: "Xóa đơn hàng thất bại",
      onSuccess: (state, payload) => {
        state.orders = state.orders.filter((o) => o._id !== payload);
      },
    });
  },
});

export const { clearOrder } = orderSlice.actions;
export default orderSlice.reducer;
